import React from "react";
import { Check, ArrowRight } from "lucide-react";
import MountainRidge from "./MountainRidge";

const BULLETS = [
  "Individuelles Training statt Standardprogramm für alle",
  "Mikronährstoff-Check deckt auf, was Training allein nicht löst",
  "Persönliche Betreuung statt 08/15-Plan aus der App",
  "Fachlich fundiert – keine pauschalen Diätvorgaben ohne Rücksicht auf dich",
  "Nachhaltige Routine statt kurzfristiger Motivation",
];

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Hero() {
  return (
    <section
      id="hero-section"
      data-testid="hero-section"
      className="relative isolate overflow-hidden bg-[#1F2937] text-[#F7F9FB] min-h-[92vh] flex items-center pt-28 pb-32 lg:pt-32 lg:pb-40"
    >
      {/* Background image */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.pexels.com/photos/25078526/pexels-photo-25078526.jpeg"
          alt=""
          className="h-full w-full object-cover object-center opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1F2937] via-[#1F2937]/85 to-[#1F2937]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F2937] via-transparent to-[#1F2937]/30" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full">
        <div className="max-w-3xl">
          <div
            data-testid="hero-personal-intro"
            className="reveal flex items-center gap-4 mb-8"
          >
            <div className="relative shrink-0">
              <span className="absolute -inset-1 rounded-full bg-[#2E89C4]/40 blur-md" />
              <img
                src="https://static.wixstatic.com/media/ec825b_9ed2e87041c048e3b1234af0a5ce6981~mv2.jpg/v1/fill/w_941,h_1105,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Alex%20Buchner-0025-%C2%A9Fotograf%20Hans%20Breiten.jpg"
                alt="Alex Buchner"
                className="relative h-14 w-14 sm:h-16 sm:w-16 rounded-full object-cover border-2 border-[#F7F9FB]/80 shadow-lg"
              />
              <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-[#6FB04F] border-2 border-[#1F2937]" />
            </div>
            <div>
              <p className="text-sm sm:text-base font-display font-bold text-[#F7F9FB]">
                Hi, ich bin Alex. 👋
              </p>
              <p className="text-xs sm:text-sm text-[#F7F9FB]/65">
                Personal Trainer & Mikronährstoffcoach im Chiemgau
              </p>
            </div>
          </div>

          <div
            data-testid="hero-eyebrow"
            className="reveal inline-flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-[#6FB04F] mb-7"
          >
            <span className="h-px w-8 bg-[#6FB04F]" />
            Personal Training · Mikronährstoffberatung · Chiemgau
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] reveal"
            style={{ animationDelay: "60ms" }}
          >
            Personal Training und{" "}
            <span className="text-[#2E89C4]">Mikronährstoffberatung</span> – für
            mehr <span className="text-[#6FB04F]">Energie</span>, Gesundheit und
            Leistungsfähigkeit im Alltag.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-7 text-base sm:text-lg lg:text-xl text-[#C7CFD9] max-w-2xl leading-relaxed reveal"
            style={{ animationDelay: "140ms" }}
          >
            Aus einer Hand – für alle, die Beschwerden nicht nur wegtrainieren,
            sondern wirklich verstehen wollen, woher sie kommen. Ganzheitlich
            statt Flickenteppich aus mehreren Experten.
          </p>

          <ul
            data-testid="hero-bullets"
            className="mt-9 grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl"
          >
            {BULLETS.map((b, i) => (
              <li
                key={i}
                data-testid={`hero-bullet-${i}`}
                className="flex items-start gap-3 reveal"
                style={{ animationDelay: `${200 + i * 60}ms` }}
              >
                <span className="mt-1 h-5 w-5 shrink-0 rounded-full bg-[#6FB04F]/20 flex items-center justify-center">
                  <Check size={12} className="text-[#6FB04F]" strokeWidth={3} />
                </span>
                <span className="text-sm sm:text-[15px] text-[#F7F9FB]/90 leading-snug">
                  {b}
                </span>
              </li>
            ))}
          </ul>

          <div
            className="mt-11 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 reveal"
            style={{ animationDelay: "560ms" }}
          >
            <button
              data-testid="hero-cta-button"
              onClick={() => scrollTo("booking-section")}
              className="btn-primary group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-base font-semibold"
            >
              Kostenloses Erstgespräch sichern
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
            <button
              data-testid="hero-secondary-button"
              onClick={() => scrollTo("product-intro-section")}
              className="btn-outline-light inline-flex items-center justify-center px-7 py-4 rounded-full text-base font-medium"
            >
              So arbeite ich
            </button>
          </div>

          <div
            data-testid="hero-trust"
            className="mt-10 text-xs sm:text-sm text-[#F7F9FB]/55 flex items-center gap-4 reveal"
            style={{ animationDelay: "660ms" }}
          >
            <span>Begrenzte Plätze pro Monat.</span>
            <span className="h-1 w-1 rounded-full bg-[#F7F9FB]/30" />
            <span>Persönlich vor Ort & online.</span>
          </div>
        </div>
      </div>

      {/* Ridge transition to next light section */}
      <div className="absolute bottom-0 inset-x-0">
        <MountainRidge fill="#F7F9FB" accent="#6FB04F" />
      </div>
    </section>
  );
}
